import { FC, useMemo } from "react";
import StarRateNewTrustScore from "../../../components/StarRate2.0";
import { scoreToDisplayStars } from "../../../config/rating";
import { IWidgetTemplate } from "../../../interfaces/widget-template";
import { ReachClick } from "../../../utils/core";
import { resolveWidgetV2Style } from "./widgetV2Styles";

const LOGO_MARK = `${process.env.PUBLIC_URL}/images/logos/logo-mark.webp`;

/**
 * QR Poster (v2) — admin-preview-only. Depends on a third-party QR image
 * API (api.qrserver.com), same simplification used in widget-v2.js's
 * renderQr — flagged there for a self-hosted replacement later. The design
 * mock uses decorative fake QR pixels since it links nowhere real; this
 * renders a real scannable code instead, since a real embed has to.
 */
const WidgetTemplate27: FC<IWidgetTemplate> = ({ company, score, style }) => {
  const companyId = company?.companyId;
  const resolvedStyle = resolveWidgetV2Style("qr", style);
  const payload = useMemo(() => ({ companyId, widgetId: 27 as const }), [companyId]);
  const profileUrl = useMemo(
    () => `https://www.trustfinance.com/companies/${companyId}?utm_source=widget&utm_medium=dom&utm_campaign=widget-v2-qr`,
    [companyId],
  );
  const qrSize = resolvedStyle === "sticker" ? 80 : resolvedStyle === "table-tent" ? 76 : 88;
  const qrSrc = useMemo(
    () => `https://api.qrserver.com/v1/create-qr-code/?size=${qrSize}x${qrSize}&data=${encodeURIComponent(profileUrl)}`,
    [profileUrl, qrSize],
  );
  const linkProps = { href: profileUrl, target: "_blank" as const, rel: "noopener", onClick: () => ReachClick(payload) };

  if (resolvedStyle === "sticker") {
    return (
      <div className="d-flex justify-content-center">
        <a {...linkProps} className="d-inline-flex align-items-center justify-content-center" style={{ borderRadius: 999, border: "4px solid #006bf1", padding: 10, background: "#fff", textDecoration: "none" }}>
          <img src={qrSrc} width={qrSize} height={qrSize} alt="Scan to view our TrustFinance profile" />
        </a>
      </div>
    );
  }

  if (resolvedStyle === "table-tent") {
    return (
      <div className="d-flex justify-content-center">
        <a
          {...linkProps}
          className="d-flex align-items-center gap-3"
          style={{ textDecoration: "none", color: "#1c1c2e", border: "1px solid #e7e8ec", borderRadius: 8, background: "#fff", boxShadow: "0 1px 2px rgba(28,28,46,0.05)", padding: "10px 16px" }}
        >
          <img src={qrSrc} width={qrSize} height={qrSize} alt="Scan to view our TrustFinance profile" />
          <span className="text-start">
            <b className="d-block" style={{ fontSize: 11 }}>How was your experience?</b>
            <span style={{ fontSize: 9.5, color: "#6b6b85" }}>Scan to review us on TrustFinance</span>
          </span>
        </a>
      </div>
    );
  }

  return (
    <div className="d-flex justify-content-center">
      <a
        {...linkProps}
        className="text-center d-block"
        style={{ textDecoration: "none", color: "#fff", background: "linear-gradient(180deg,#006bf1,#0038db)", borderRadius: 8, padding: 12 }}
      >
        <span className="d-flex align-items-center justify-content-center gap-1">
          <img src={LOGO_MARK} alt="" width={13} height={13} />
          <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.04em", opacity: 0.9 }}>RATE US ON TRUSTFINANCE</span>
        </span>
        <span className="d-inline-block" style={{ background: "#fff", borderRadius: 6, padding: 6, marginTop: 6 }}>
          <img src={qrSrc} width={qrSize} height={qrSize} alt="Scan to view our TrustFinance profile" />
        </span>
        <span className="d-flex align-items-center justify-content-center gap-1" style={{ marginTop: 6 }}>
          <StarRateNewTrustScore value={score?.score} size={10} />
          <span style={{ fontSize: 9 }}>{scoreToDisplayStars(score?.score ?? 0)} · {(score?.totalReview ?? 0).toLocaleString()} reviews</span>
        </span>
      </a>
    </div>
  );
};

export default WidgetTemplate27;
